import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AsyncFeedbackService } from '@shared/services/async-feedback.service';
import { TranslateService } from '@ngx-translate/core';
import { IconType } from '@shared/enums';
import { isSilentBidChatCall } from '@features/bid-chat/constants/bid-chat.constants';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  constructor(
    private asyncFeedbackService: AsyncFeedbackService,
    private translationService: TranslateService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Hide error for any 401 – access-token expiry is handled silently by
        // TokenInterceptor (refresh flow), and refresh-token 401 triggers re-login.
        if (error.status === 401) {
          return throwError(error);
        }

        if (error.status === 404 && req.url.includes('request-phone-login-otp')) {
          return throwError(error);
        }

        // Bid chat (#47189): these calls handle failure in place — the token only means live chat won't
        // load, History falls back silently, accept shows an inline error. initiate-bid is not here: its
        // errors (e.g. BidAlreadyProcessed) are permanent, so the user needs the server's reason.
        if (isSilentBidChatCall(req.url)) {
          return throwError(error);
        }

        const traceId = error?.headers?.get('x-trace-id') ?? '';

        const errorMessage = `${this.translationService.instant('ErrorMessages.TraceId', {
          traceId,
        })} </br> ${
          error.error?.errors?.[0] || this.translationService.instant('ErrorMessages.ErrorHappened')
        }`;

        this.asyncFeedbackService.showFailureMessage(errorMessage, null, null, {
          tooltip: this.translationService.instant('Buttons.CopyToClipboard'),
          handler: () => {
            navigator.clipboard.writeText(traceId);
          },
          icon: { name: 'outline-copy', type: IconType.SVG, position: 'left' },
        });

        return throwError(error);
      })
    );
  }
}
